// Toolbar.tsx
import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
  SxProps,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useNavigate } from "react-router-dom";
import Sandbox from "./Sandbox";
import Export from "./Export";
import Labeler from "./Labeler";
import SaveAs from "./SaveAs";

// These constants match your hex geometry as used in Canvas.tsx.
const HEX_WIDTH = 300;
const HEX_HEIGHT = 260;

interface ToolbarProps {
  boundingBox: { minRow: number; maxRow: number; minCol: number; maxCol: number };
  setBoundingBox: (newMinR: number, newMaxR: number, newMinC: number, newMaxC: number) => void;
  resetMap: () => void;
  showDPad: boolean;
  setShowDPad: (value: boolean) => void;
  showLocator: boolean;
  setShowLocator: (value: boolean) => void;
  onTileSelect?: (selection: { L0: string | null; L1: string | null; L3: boolean }) => void;
}

const Toolbar: React.FC<ToolbarProps> = ({
  boundingBox,
  setBoundingBox,
  resetMap,
  showDPad,
  setShowDPad,
  showLocator,
  setShowLocator,
  onTileSelect,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [openResetDialog, setOpenResetDialog] = React.useState(false);

  // State to control sidebar open/closed
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  // Button style using theme colors
  const buttonSx: SxProps = {
    minWidth: isMobile ? 32 : 40,
    minHeight: isMobile ? 32 : 40,
    fontSize: isMobile ? "0.8rem" : "1rem",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  };

  // Determine width based on open state
  const sidebarWidth = sidebarOpen ? (isMobile ? "150px" : "250px") : "40px";

  return (
    <Box sx={{ position: "relative", display: "flex" }}>
      <Box
        sx={{
          position: "relative", // Make exit button absolute relative to sidebar
          width: sidebarWidth,
          flexShrink: 0,
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.text.primary,
          p: sidebarOpen ? 2 : 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
          transition: "width 0.3s ease, padding 0.3s ease",
          overflow: "hidden",
        }}
      >
        {/* Exit Button placed at the top left inside the sidebar */}
        <IconButton
          onClick={() => navigate("/")}
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
            opacity: sidebarOpen ? 1 : 0,
            transition: "opacity 0.3s ease",
            zIndex: 1000,
          }}
        >
          <ExitToAppIcon />
        </IconButton>

        {/* Toolbar header */}
        <Box sx={{ opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.3s ease" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Map Controls
          </Typography>
        </Box>

        {/* Reset Button with Confirmation Dialog */}
        <Box sx={{ width: "100%", opacity: sidebarOpen ? 1 : 0, transition: "opacity 0.3s ease" }}>
          <Button variant="contained" sx={buttonSx} fullWidth onClick={() => setOpenResetDialog(true)}>
            Reset
          </Button>
          <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
            <DialogTitle>Confirm Reset</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to reset the map to default values?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenResetDialog(false)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  resetMap();
                  setOpenResetDialog(false);
                }}
                color="primary"
                autoFocus
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* Scrollable Toolbox Content with scrollbar on the left */}
        <Box
          sx={{
            flexGrow: 1,
            width: "100%",
            overflowY: sidebarOpen ? "auto" : "hidden",
            overflowX: "hidden", // Hide horizontal scrollbar
            pl: 1, // use left padding for the scrollbar position
            direction: "rtl", // moves the scrollbar to the left
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent", // makes the track invisible
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.primary.main, // uses theme primary color
              borderRadius: "3px",
            },
          }}
        >
          {/* Wrap content in an inner Box with ltr to keep content layout normal */}
          <Box sx={{ direction: "ltr" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                opacity: sidebarOpen ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            >
              {/* Toggle Switch for DPad */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showDPad}
                    onChange={(e) => setShowDPad(e.target.checked)}
                    color="primary"
                  />
                }
                label={showDPad ? "Hide DPad" : "Show DPad"}
              />

              {/* Toggle Switch for Locator */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showLocator}
                    onChange={(e) => setShowLocator(e.target.checked)}
                    color="primary"
                  />
                }
                label={showLocator ? "Hide Locator" : "Show Locator"}
              />

              {/* Tile Textures Section */}
              <Box sx={{ width: "100%", mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, width: "100%", textAlign: "center" }}>
                  Tile Textures
                </Typography>
                <Sandbox onTileSelect={onTileSelect} />
              </Box>
              <Box sx={{ width: "100%", textAlign: "center" }}>
              <Export />
              </Box>
              <Box sx={{ width: "100%", textAlign: "center" }}>
              <SaveAs />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Arrow toggle button */}
      <IconButton
        onClick={() => setSidebarOpen((prev) => !prev)}
        sx={{
          position: "absolute",
          top: "50%",
          right: -16,
          transform: "translateY(-50%)",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          borderRadius: "50%",
          width: 32,
          height: 32,
          boxShadow: 3,
          transition: "transform 0.3s ease",
          zIndex: 1,
        }}
      >
        {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
      </IconButton>
    </Box>
  );
};

export default Toolbar;
